import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { ArrowRight, Users, CreditCard, Bell, TrendingUp, Sparkles, Moon, Sun, BookOpen, Gift, Plane, PieChart, ChevronDown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_team-expense/artifacts/1f23zen4_Screenshot%202026-01-29%20at%2010.35.14%E2%80%AFPM.png";

const Landing = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const [stats, setStats] = useState({
    saved_formatted: '₹0',
    users_formatted: '0',
    groups_formatted: '0',
    rating_formatted: '4.9★'
  });
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme');
      return saved ? saved === 'dark' : true;
    }
    return true;
  });

  // Parallax effects
  const heroY = useTransform(scrollY, [0, 500], [0, 150]);
  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0]);

  useEffect(() => {
    fetchStats();
    // Apply theme
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/platform/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  const features = [
    {
      icon: <Users className="w-7 h-7" />,
      title: "Group Savings",
      description: "Create groups for any shared goal. Trips, rent, gifts, or emergency funds.",
      gradient: "from-violet-500 to-purple-600"
    },
    {
      icon: <CreditCard className="w-7 h-7" />,
      title: "UPI Payments",
      description: "Pay directly via any UPI app. Instant, secure settlements.",
      gradient: "from-emerald-500 to-teal-600"
    },
    {
      icon: <Bell className="w-7 h-7" />,
      title: "Smart Reminders",
      description: "Automated reminders ensure everyone pays on time.",
      gradient: "from-orange-500 to-amber-600"
    },
    {
      icon: <TrendingUp className="w-7 h-7" />,
      title: "Gamification",
      description: "Earn XP, unlock badges, and climb the leaderboard.",
      gradient: "from-pink-500 to-rose-600"
    }
  ];

  const useCases = [
    { icon: <Plane className="w-5 h-5" />, title: "Trip Planning", desc: "Save for dream vacations" },
    { icon: <Users className="w-5 h-5" />, title: "Rent Splitting", desc: "Share with roommates" },
    { icon: <Gift className="w-5 h-5" />, title: "Group Gifts", desc: "Pool for special occasions" },
    { icon: <PieChart className="w-5 h-5" />, title: "Bill Sharing", desc: "Split any expense" }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-500 ${isDark ? 'bg-[#000000]' : 'bg-[#fbfbfd]'}`}>
      <Helmet>
        <title>SharePay India - Split Bills & Save Together with UPI | sharepay.today</title>
        <meta name="description" content="India's #1 gamified group savings app. Split expenses via UPI, save with friends, earn XP rewards. 100% Free. Made in India." />
        <link rel="canonical" href="https://sharepay.today/" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Stable Rupee Symbol Overlays */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {/* Large Rupee - Top Left */}
        <div 
          className={`absolute top-[10%] left-[5%] text-[280px] font-bold select-none ${
            isDark ? 'text-violet-500/[0.04]' : 'text-violet-500/[0.03]'
          }`}
          style={{ fontFamily: 'system-ui' }}
        >
          ₹
        </div>
        
        {/* Medium Rupee - Bottom Right */}
        <div 
          className={`absolute bottom-[5%] right-[8%] text-[200px] font-bold select-none ${
            isDark ? 'text-cyan-500/[0.05]' : 'text-cyan-500/[0.04]'
          }`}
          style={{ fontFamily: 'system-ui' }}
        >
          ₹
        </div>
        
        {/* Small Rupee - Center Right */}
        <div 
          className={`absolute top-[45%] right-[3%] text-[120px] font-bold select-none ${
            isDark ? 'text-pink-500/[0.04]' : 'text-pink-500/[0.03]'
          }`}
          style={{ fontFamily: 'system-ui' }}
        >
          ₹
        </div>
      </div>

      {/* Glossy gradient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Main gradient orb */}
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.4, 0.3],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className={`absolute top-[-30%] right-[-20%] w-[800px] h-[800px] rounded-full blur-[120px] ${
            isDark ? 'bg-violet-600/30' : 'bg-violet-400/20'
          }`}
        />
        <motion.div
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className={`absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full blur-[100px] ${
            isDark ? 'bg-cyan-600/20' : 'bg-cyan-400/15'
          }`}
        />
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 4 }}
          className={`absolute top-[40%] left-[40%] w-[400px] h-[400px] rounded-full blur-[80px] ${
            isDark ? 'bg-pink-600/10' : 'bg-pink-400/10'
          }`}
        />
      </div>

      {/* Navigation - Apple-style glass */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl border-b transition-colors duration-300 ${
          isDark 
            ? 'bg-black/70 border-white/10' 
            : 'bg-white/70 border-black/5'
        }`}
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex justify-between items-center h-14">
            <motion.div 
              className="flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <img src={LOGO_URL} alt="SharePay" className="w-8 h-8 object-contain rounded-lg" />
              <span className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>SharePay</span>
            </motion.div>
            
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className={`rounded-full w-9 h-9 transition-colors ${
                  isDark ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-black/5'
                }`}
                data-testid="theme-toggle-btn"
              >
                <motion.div
                  initial={false}
                  animate={{ rotate: isDark ? 0 : 180 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                >
                  {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </motion.div>
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="ghost"
                className={`rounded-full text-sm px-4 hidden sm:flex ${
                  isDark ? 'text-gray-300 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-black/5'
                }`}
                data-testid="signin-btn"
              >
                Sign in
              </Button>
              <Button
                onClick={() => navigate('/signup')}
                className="rounded-full bg-violet-600 hover:bg-violet-500 text-white text-sm px-5 shadow-lg shadow-violet-600/25 hover:shadow-violet-500/30 transition-all hover:-translate-y-0.5"
                data-testid="signup-btn"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-14 overflow-hidden">
        <motion.div 
          style={{ y: heroY, opacity: heroOpacity }}
          className="relative z-10 max-w-6xl mx-auto px-6 text-center"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium backdrop-blur-xl border ${
              isDark 
                ? 'bg-white/5 border-white/10 text-gray-300' 
                : 'bg-black/5 border-black/5 text-gray-700'
            }`}>
              <Sparkles className="w-4 h-4 text-violet-500" />
              Trusted by {stats.users_formatted}+ users across India
            </span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className={`text-5xl sm:text-6xl lg:text-7xl font-bold mt-8 mb-6 tracking-tight ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            Money with Friends.
            <br />
            <span className="bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
              Gamified.
            </span>
          </motion.h1>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className={`text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            Split expenses, save together, and achieve financial goals with your squad. 
            The smart way Indians manage group money.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
          >
            <Button
              onClick={() => navigate('/signup')}
              className="group rounded-full bg-violet-600 hover:bg-violet-500 text-white px-8 py-6 text-lg font-medium shadow-2xl shadow-violet-600/30 hover:shadow-violet-500/40 transition-all hover:-translate-y-1"
              data-testid="hero-cta-btn"
            >
              Start Free 
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => navigate('/guide')}
              variant="ghost"
              className={`rounded-full px-8 py-6 text-lg font-medium border-2 transition-all hover:-translate-y-1 ${
                isDark 
                  ? 'border-white/20 text-white hover:bg-white/5 hover:border-white/30' 
                  : 'border-gray-200 text-gray-900 hover:bg-gray-50 hover:border-gray-300'
              }`}
              data-testid="guide-btn"
            >
              <BookOpen className="w-5 h-5 mr-2" /> How It Works
            </Button>
          </motion.div>

          {/* Stats - Glossy cards */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto"
          >
            {[
              { value: stats.saved_formatted, label: "Money Saved", sublabel: "by our community", gradient: "from-violet-500/20 to-purple-500/20" },
              { value: `${stats.users_formatted}+`, label: "Happy Users", sublabel: "and growing", gradient: "from-emerald-500/20 to-teal-500/20" },
              { value: `${stats.groups_formatted}+`, label: "Active Groups", sublabel: "saving together", gradient: "from-cyan-500/20 to-blue-500/20" },
              { value: stats.rating_formatted, label: "User Rating", sublabel: "trusted platform", gradient: "from-pink-500/20 to-rose-500/20" }
            ].map((stat, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
                className={`relative rounded-2xl p-5 backdrop-blur-xl border overflow-hidden ${
                  isDark 
                    ? 'bg-white/5 border-white/10' 
                    : 'bg-white/60 border-white shadow-lg shadow-black/5'
                }`}
              >
                {/* Glossy shine effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-50`} />
                <div className={`absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b ${isDark ? 'from-white/10' : 'from-white/80'} to-transparent`} />
                
                <div className="relative">
                  <p className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {stat.value}
                  </p>
                  <p className={`text-sm font-medium mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {stat.label}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    {stat.sublabel}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className={`${isDark ? 'text-gray-500' : 'text-gray-400'}`}
            >
              <ChevronDown className="w-6 h-6" />
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Use Cases Section */}
      <section className={`py-24 px-6 relative ${isDark ? 'bg-black' : 'bg-gray-50/50'}`}>
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-violet-500 text-sm font-semibold tracking-[0.15em] uppercase">Use Cases</span>
            <h2 className={`text-3xl sm:text-4xl font-bold mt-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Built for Every Situation
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((uc, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className={`group relative rounded-3xl p-6 backdrop-blur-xl border overflow-hidden cursor-pointer transition-all duration-500 ${
                  isDark 
                    ? 'bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10 hover:border-violet-500/50' 
                    : 'bg-white border-gray-200 hover:border-violet-300 shadow-lg shadow-black/5 hover:shadow-xl'
                }`}
              >
                {/* Glossy top shine */}
                <div className={`absolute top-0 left-0 right-0 h-24 bg-gradient-to-b ${isDark ? 'from-white/5' : 'from-white'} to-transparent opacity-80`} />
                
                <div className="relative">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110 ${
                    isDark ? 'bg-violet-500/20 text-violet-400' : 'bg-violet-100 text-violet-600'
                  }`}>
                    {uc.icon}
                  </div>
                  <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {uc.title}
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {uc.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={`py-24 px-6 ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-violet-500 text-sm font-semibold tracking-[0.15em] uppercase">Features</span>
            <h2 className={`text-3xl sm:text-4xl font-bold mt-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Everything You Need
            </h2>
            <p className={`mt-4 max-w-xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Powerful features designed for the way Indians manage money together.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                whileHover={{ y: -5 }}
                className={`group relative rounded-3xl p-8 backdrop-blur-xl border overflow-hidden transition-all duration-500 ${
                  isDark 
                    ? 'bg-gradient-to-br from-white/[0.08] to-transparent border-white/10 hover:border-white/20' 
                    : 'bg-white border-gray-100 hover:border-gray-200 shadow-xl shadow-black/5'
                }`}
              >
                {/* Glossy effect */}
                <div className={`absolute top-0 left-0 right-0 h-32 bg-gradient-to-b ${isDark ? 'from-white/5' : 'from-gray-50'} to-transparent`} />
                
                <div className="relative flex gap-5">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-white shadow-lg shrink-0 group-hover:scale-110 transition-transform`}>
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {feature.title}
                    </h3>
                    <p className={`leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {feature.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative rounded-[2rem] bg-gradient-to-br from-violet-600 via-purple-600 to-pink-600 p-12 sm:p-16 overflow-hidden"
          >
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent opacity-50" />
            <div className="absolute top-0 left-0 right-0 h-1/3 bg-gradient-to-b from-white/30 to-transparent" />
            
            {/* Animated orbs */}
            <motion.div
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/20 blur-3xl -translate-y-1/2 translate-x-1/2"
            />
            
            <div className="relative text-center text-white">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Ready to Save Smarter?
              </h2>
              <p className="text-white/80 text-lg mb-10 max-w-lg mx-auto">
                Join thousands of Indians managing group finances the easy way. It's completely free.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/signup')}
                  className="rounded-full bg-white text-violet-600 hover:bg-gray-100 font-semibold px-8 py-6 text-lg shadow-2xl hover:shadow-white/20 transition-all hover:-translate-y-1"
                  data-testid="cta-btn"
                >
                  Create Free Account
                </Button>
                <Button 
                  onClick={() => navigate('/guide')}
                  variant="ghost"
                  className="rounded-full border-2 border-white/40 text-white hover:bg-white/10 hover:border-white/60 px-8 py-6 text-lg font-medium"
                >
                  <BookOpen className="w-5 h-5 mr-2" /> Read Guide
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-12 px-6 border-t ${isDark ? 'border-white/5 bg-black' : 'border-gray-100 bg-gray-50'}`}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src={LOGO_URL} alt="SharePay" className="w-8 h-8 object-contain rounded-lg" />
                <span className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>SharePay</span>
              </div>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Save Together, Achieve Together. The smart way Indians manage group finances.
              </p>
              {/* Social Links */}
              <div className="flex gap-3">
                <a 
                  href="https://www.instagram.com/sharepay.today" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110 ${
                    isDark ? 'bg-white/10 hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500' : 'bg-gray-100 hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 hover:text-white'
                  }`}
                  aria-label="Instagram"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a 
                  href="https://www.linkedin.com/company/sharepaytoday" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110 ${
                    isDark ? 'bg-white/10 hover:bg-blue-600' : 'bg-gray-100 hover:bg-blue-600 hover:text-white'
                  }`}
                  aria-label="LinkedIn"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
                <a 
                  href="https://www.reddit.com/r/SharePayIndia/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110 ${
                    isDark ? 'bg-white/10 hover:bg-orange-500' : 'bg-gray-100 hover:bg-orange-500 hover:text-white'
                  }`}
                  aria-label="Reddit"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>
                </a>
              </div>
            </div>
            
            {/* Quick Links */}
            <div>
              <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Product</h4>
              <ul className={`space-y-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <li><a href="/guide" className={`transition-colors ${isDark ? 'hover:text-white' : 'hover:text-gray-900'}`}>How It Works</a></li>
                <li><a href="/signup" className={`transition-colors ${isDark ? 'hover:text-white' : 'hover:text-gray-900'}`}>Get Started</a></li>
                <li><a href="/login" className={`transition-colors ${isDark ? 'hover:text-white' : 'hover:text-gray-900'}`}>Login</a></li>
              </ul>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Legal</h4>
              <ul className={`space-y-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <li><a href="/privacy" className={`transition-colors ${isDark ? 'hover:text-white' : 'hover:text-gray-900'}`}>Privacy Policy</a></li>
                <li><a href="/terms" className={`transition-colors ${isDark ? 'hover:text-white' : 'hover:text-gray-900'}`}>Terms of Service</a></li>
                <li><a href="/support" className={`transition-colors ${isDark ? 'hover:text-white' : 'hover:text-gray-900'}`}>Support</a></li>
              </ul>
            </div>
            
            {/* Support */}
            <div>
              <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Get Help</h4>
              <ul className={`space-y-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <li>
                  <a 
                    href="https://www.instagram.com/sharepay.today" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 transition-colors ${isDark ? 'hover:text-white' : 'hover:text-gray-900'}`}
                  >
                    <svg className="w-4 h-4 text-pink-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                    Instant Support (DM)
                  </a>
                </li>
                <li><a href="mailto:support@sharepay.today" className={`transition-colors ${isDark ? 'hover:text-white' : 'hover:text-gray-900'}`}>support@sharepay.today</a></li>
              </ul>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className={`pt-8 border-t ${isDark ? 'border-white/5' : 'border-gray-200'} flex flex-col md:flex-row justify-between items-center gap-4`}>
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              © 2026 SharePay. Save Together, Achieve Together.
            </p>
            <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
              Made with ❤️ in India
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
